/**
 * Onglet "Rapports d'opérations" — réservé admins.
 *
 * Affiche tous les batches d'opérations en masse récents (notes_devoirs +
 * notes_examens) :
 *   - Reports examen→devoir, devoir→examen
 *   - Notes collectives
 *   - Vidages
 *   - Copies entre matières
 *   - Marquages absent vides
 *   - Etc.
 *
 * Chaque batch peut être consulté en détail (qui, quand, combien, motif) et
 * éventuellement annulé via /annuler-batch/.
 */
import React, { useState, useMemo } from 'react';
import {
  Loader, Filter, Download, RefreshCw, ArrowLeftRight,
  ArrowRightLeft, AlertTriangle, Eraser, Award, FileQuestion,
  UserX, Copy, Clock, User, Eye, Undo2, X, BookOpen, Library,
} from 'lucide-react';
import {
  useGetBatchesRecentsQuery as useGetBatchesRecentsDevoirsQuery,
  useAnnulerBatchMutation as useAnnulerBatchDevoirsMutation,
} from '../../services/notesDevoirsApi';
import {
  useGetBatchesRecentsExamensQuery,
  useAnnulerBatchExamenMutation,
} from '../../services/notesExamensApi';

interface UnifiedBatch {
  source: 'devoir' | 'examen';
  batch_id: string;
  type_operation: string;
  date: string;
  nb_modifications: number;
  auteur: string | null;
  matiere_code: string | null;
  matiere_nom: string | null;
  commentaire: string;
  annule: boolean;
}

const TYPE_META: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  report_examen_vers_devoir: { label: 'Report Examen → Devoirs', icon: <ArrowRightLeft size={11} />, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  report_devoir_vers_examen: { label: 'Report Devoir → Examen', icon: <ArrowLeftRight size={11} />, color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300' },
  note_collective: { label: 'Note collective', icon: <Award size={11} />, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
  note_collective_examen: { label: 'Note collective examen', icon: <Award size={11} />, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
  vider_notes_devoirs: { label: 'Vidage notes devoirs', icon: <Eraser size={11} />, color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
  copie_notes_entre_matieres: { label: 'Copie matière → matière', icon: <Copy size={11} />, color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300' },
  attribution_zero_absent: { label: 'Marquer absents (vides)', icon: <UserX size={11} />, color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
  annulation_batch: { label: 'Annulation batch', icon: <Undo2 size={11} />, color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
};

const RapportsOperationsTab: React.FC = () => {
  const [filterType, setFilterType] = useState('');
  const [filterSource, setFilterSource] = useState<'tous' | 'devoir' | 'examen'>('tous');
  const [searchUser, setSearchUser] = useState('');
  const [showAnnuleeRow, setShowAnnulee] = useState(true);
  const [detailBatch, setDetailBatch] = useState<UnifiedBatch | null>(null);

  const { data: batchesDev, isFetching: loadingDev, refetch: refetchDev } =
    useGetBatchesRecentsDevoirsQuery({ limit: 200 });
  const { data: batchesEx, isFetching: loadingEx, refetch: refetchEx } =
    useGetBatchesRecentsExamensQuery({ limit: 200 });

  const [annulerDev, { isLoading: annulerDevLoading }] = useAnnulerBatchDevoirsMutation();
  const [annulerEx, { isLoading: annulerExLoading }] = useAnnulerBatchExamenMutation();
  const annulerLoading = annulerDevLoading || annulerExLoading;

  const allBatches: UnifiedBatch[] = useMemo(() => {
    const out: UnifiedBatch[] = [];
    (batchesDev?.batches || []).forEach((b: any) => out.push({ source: 'devoir', ...b }));
    (batchesEx?.batches || []).forEach((b: any) => out.push({ source: 'examen', ...b }));
    out.sort((a, b) => b.date.localeCompare(a.date));
    return out;
  }, [batchesDev, batchesEx]);

  const filteredBatches = useMemo(() => {
    let r = allBatches;
    if (filterSource !== 'tous') r = r.filter(b => b.source === filterSource);
    if (filterType) r = r.filter(b => b.type_operation === filterType);
    if (searchUser.trim()) {
      const q = searchUser.trim().toLowerCase();
      r = r.filter(b => (b.auteur || '').toLowerCase().includes(q));
    }
    if (!showAnnuleeRow) r = r.filter(b => !b.annule);
    return r;
  }, [allBatches, filterSource, filterType, searchUser, showAnnuleeRow]);

  const stats = useMemo(() => {
    const totalOps = filteredBatches.length;
    const totalModifs = filteredBatches.reduce((s, b) => s + (b.nb_modifications || 0), 0);
    const parUtilisateur = new Map<string, { ops: number; modifs: number }>();
    for (const b of filteredBatches) {
      const k = b.auteur || '— inconnu —';
      const cur = parUtilisateur.get(k) || { ops: 0, modifs: 0 };
      cur.ops += 1;
      cur.modifs += b.nb_modifications || 0;
      parUtilisateur.set(k, cur);
    }
    const topUsers = Array.from(parUtilisateur.entries()).sort((a, b) => b[1].modifs - a[1].modifs).slice(0, 8);
    return { totalOps, totalModifs, topUsers };
  }, [filteredBatches]);

  const handleAnnulerBatch = async (b: UnifiedBatch) => {
    if (!window.confirm(
      `⚠ Annuler ce batch ?\n\n` +
      `Type : ${TYPE_META[b.type_operation]?.label || b.type_operation}\n` +
      `Auteur : ${b.auteur}\n` +
      `Modifications : ${b.nb_modifications}\n\n` +
      `Toutes les valeurs seront restaurées à leur état avant le batch. Une nouvelle entrée d'historique sera créée pour tracer cette annulation.`
    )) return;
    try {
      if (b.source === 'devoir') {
        await annulerDev({ batch_id: b.batch_id }).unwrap();
      } else {
        await annulerEx({ batch_id: b.batch_id }).unwrap();
      }
      alert('Batch annulé avec succès. Les anciennes valeurs ont été restaurées.');
      refetchDev();
      refetchEx();
      setDetailBatch(null);
    } catch (e: any) {
      alert(`Erreur : ${e?.data?.error || e?.message || 'Échec de l\'annulation'}`);
    }
  };

  const exportCSV = () => {
    const cols = ['date', 'source', 'type_operation', 'matiere_code', 'matiere_nom', 'auteur', 'nb_modifications', 'annule', 'commentaire', 'batch_id'];
    const lines = [
      cols.join(';'),
      ...filteredBatches.map(b => [
        new Date(b.date).toLocaleString('fr-FR'),
        b.source,
        TYPE_META[b.type_operation]?.label || b.type_operation,
        b.matiere_code || '',
        b.matiere_nom || '',
        b.auteur || '',
        b.nb_modifications,
        b.annule ? 'OUI' : '',
        (b.commentaire || '').replace(/[;\n\r]/g, ' '),
        b.batch_id,
      ].map(v => String(v ?? '').replace(/[;\n\r]/g, ' ')).join(';')),
    ];
    const csv = '﻿' + lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    a.href = url;
    a.download = `rapports_operations_${ts}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Liste unique des types présents (pour le filtre)
  const typesDispo = useMemo(() => {
    const s = new Set(allBatches.map(b => b.type_operation));
    return Array.from(s).sort();
  }, [allBatches]);

  const loading = loadingDev || loadingEx;

  return (
    <div className="space-y-4">
      {/* En-tête + intro */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 flex items-start gap-3">
        <Library size={20} className="text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm">
          <div className="font-semibold text-amber-900 dark:text-amber-200">Rapports d'opérations en masse</div>
          <div className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
            Historique de tous les batches : reports examen↔devoir, notes collectives, vidages, copies, etc.
            Chaque batch peut être consulté en détail et annulé pour restaurer les valeurs précédentes.
            <strong> Cet onglet est réservé aux administrateurs.</strong>
          </div>
        </div>
      </div>

      {/* Stats globales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Opérations affichées</div>
          <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.totalOps}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Modifications totales</div>
          <div className="mt-1 text-2xl font-bold text-blue-700 dark:text-blue-400">{stats.totalModifs}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Top auteur</div>
          <div className="mt-1 text-sm font-bold text-gray-900 dark:text-gray-100 truncate">
            {stats.topUsers[0] ? `${stats.topUsers[0][0]}` : '—'}
          </div>
          <div className="text-xs text-gray-500">
            {stats.topUsers[0] ? `${stats.topUsers[0][1].modifs} modifications` : 'Aucune'}
          </div>
        </div>
      </div>

      {/* Top 8 utilisateurs */}
      {stats.topUsers.length > 0 && (
        <div className="bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-200 dark:border-indigo-800 rounded-lg p-3">
          <div className="text-xs font-semibold text-indigo-700 dark:text-indigo-300 mb-2">Top 8 utilisateurs (par nb de modifications)</div>
          <div className="flex flex-wrap gap-2">
            {stats.topUsers.map(([nom, info]) => (
              <span key={nom} className="px-2 py-1 bg-white dark:bg-gray-800 border border-indigo-200 dark:border-indigo-700 rounded text-xs">
                <span className="font-semibold">{nom}</span>
                <span className="text-indigo-700 dark:text-indigo-300 ml-1">{info.modifs} modifs</span>
                <span className="text-gray-400 ml-1">({info.ops} ops)</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 flex items-center gap-2 flex-wrap text-xs">
        <Filter size={14} className="text-gray-500" />
        <select value={filterSource} onChange={e => setFilterSource(e.target.value as any)}
          className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
          <option value="tous">Tous types (devoir + examen)</option>
          <option value="devoir">Devoirs uniquement</option>
          <option value="examen">Examens uniquement</option>
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
          <option value="">Toutes opérations</option>
          {typesDispo.map(t => (
            <option key={t} value={t}>{TYPE_META[t]?.label || t}</option>
          ))}
        </select>
        <input
          type="text"
          value={searchUser}
          onChange={e => setSearchUser(e.target.value)}
          placeholder="Filtrer par utilisateur…"
          className="flex-1 min-w-[160px] px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
        />
        <label className="flex items-center gap-1 cursor-pointer text-gray-600 dark:text-gray-400">
          <input type="checkbox" checked={showAnnuleeRow} onChange={e => setShowAnnulee(e.target.checked)} className="rounded" />
          Voir aussi les batches annulés
        </label>
        <button onClick={() => { refetchDev(); refetchEx(); }}
          className="flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Actualiser
        </button>
        <button onClick={exportCSV} disabled={filteredBatches.length === 0}
          className="flex items-center gap-1 px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50">
          <Download size={12} /> Export CSV
        </button>
      </div>

      {/* Tableau */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        {loading ? (
          <div className="text-center py-8">
            <Loader className="animate-spin mx-auto text-blue-600" size={28} />
          </div>
        ) : filteredBatches.length === 0 ? (
          <div className="text-center py-12 text-sm text-gray-500">Aucun batch avec ces filtres.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr className="text-[10px] uppercase tracking-wide text-gray-600 dark:text-gray-400">
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-center">Source</th>
                <th className="px-3 py-2 text-left">Matière</th>
                <th className="px-3 py-2 text-left">Auteur</th>
                <th className="px-3 py-2 text-center">Modifs</th>
                <th className="px-3 py-2 text-left">Commentaire</th>
                <th className="px-3 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredBatches.map(b => {
                const meta = TYPE_META[b.type_operation] || { label: b.type_operation, icon: <BookOpen size={11} />, color: 'bg-gray-100 text-gray-700' };
                return (
                  <tr key={`${b.source}-${b.batch_id}`}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 ${b.annule ? 'opacity-50' : ''}`}>
                    <td className="px-3 py-1.5 font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      {new Date(b.date).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td className="px-3 py-1.5">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${meta.color}`}>
                        {meta.icon} {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                        b.source === 'devoir'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                          : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                      }`}>{b.source === 'devoir' ? 'Dev' : 'Ex'}</span>
                    </td>
                    <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300 max-w-[200px] truncate" title={b.matiere_nom || ''}>
                      {b.matiere_code ? <span className="font-mono font-semibold">{b.matiere_code}</span> : ''}{' '}
                      {b.matiere_nom || ''}
                    </td>
                    <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">
                      <User size={10} className="inline mr-1 text-gray-400" /> {b.auteur || '—'}
                    </td>
                    <td className="px-3 py-1.5 text-center font-bold text-blue-700 dark:text-blue-400">
                      {b.nb_modifications}
                    </td>
                    <td className="px-3 py-1.5 text-gray-600 dark:text-gray-400 max-w-[200px] truncate italic" title={b.commentaire}>
                      {b.commentaire || '—'}
                    </td>
                    <td className="px-3 py-1.5 text-center whitespace-nowrap">
                      {b.annule ? (
                        <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                          ✗ Annulé
                        </span>
                      ) : (
                        <button onClick={() => handleAnnulerBatch(b)}
                          disabled={annulerLoading}
                          className="px-2 py-0.5 text-[10px] bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded hover:bg-orange-200 disabled:opacity-50"
                          title="Annuler ce batch (restaurer les valeurs précédentes)">
                          <Undo2 size={10} className="inline mr-0.5" /> Annuler
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default RapportsOperationsTab;
